import { useEffect, useState } from "react";
import { CalendarSessionOverview } from "../../../-education/model/calendar-session-overview";
import { getCalendarSessions } from "../../../-education/service/session-attendance-service";
import dayjs, { Dayjs } from "dayjs";
import { Box, ToggleButton, ToggleButtonGroup, Typography, Card, CardContent, Chip, Stack } from "@mui/material";

export default function StudentCalendar({ cohortId }: { cohortId: number }) {
    const [sessions, setSessions] = useState<CalendarSessionOverview[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'daily' | 'weekly'>('weekly');
    const [referenceDate, setReferenceDate] = useState<Dayjs>(dayjs());

    const from = view === 'weekly' ? referenceDate.startOf('week').add(1, 'day') : referenceDate.startOf('day');
    const to = view === 'weekly' ? referenceDate.startOf('week').add(6, 'day') : referenceDate.endOf('day');

    useEffect(() => {
        setLoading(true);
        getCalendarSessions(cohortId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')).then(result => {
            setSessions(result ?? []);
            setLoading(false);
        }).catch(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')]);

    const navigate = (direction: number) => {
        const unit = view === 'weekly' ? 'week' : 'day';
        setReferenceDate(referenceDate.add(direction, unit));
    };

    const dateLabel = view === 'weekly'
        ? `${from.format('MMM D')} - ${to.format('MMM D, YYYY')}`
        : referenceDate.format('dddd, MMM D, YYYY');

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, v) => v && setView(v)}
                    size="small"
                >
                    <ToggleButton value="daily">Daily</ToggleButton>
                    <ToggleButton value="weekly">Weekly</ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label="←" onClick={() => navigate(-1)} clickable size="small" />
                    <Typography variant="subtitle1" sx={{ minWidth: 180, textAlign: 'center' }}>
                        {dateLabel}
                    </Typography>
                    <Chip label="→" onClick={() => navigate(1)} clickable size="small" />
                    <Chip label="Today" onClick={() => setReferenceDate(dayjs())} clickable size="small" variant="outlined" />
                </Box>
            </Box>

            {loading && <Typography>Loading...</Typography>}

            {!loading && sessions.length === 0 && (
                <Typography color="text.secondary">No sessions scheduled for this period.</Typography>
            )}

            {!loading && sessions.length > 0 && (
                <Stack spacing={1}>
                    {sessions.map((session, idx) => (
                        <Card key={idx} variant="outlined">
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2">{session.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(session.day).format('ddd, MMM D')}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={`${session.startTime} - ${session.endTime}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
